'use client';

import { FC } from 'react';

import { CtaLink, Paragraph } from '@purpur/library';
import { IconFacebook } from '@purpur/library/icon/facebook';
import { IconInstagram } from '@purpur/library/icon/instagram';
import { IconLinkedin } from '@purpur/library/icon/linkedin';
import { IconX } from '@purpur/library/icon/x';
import { IconYoutube } from '@purpur/library/icon/youtube';

import {
    AuthorDetailsContent,
    SocialButtons,
    StyledAuthorDetails,
    Title,
    TitleWithImage,
} from './AuthorDetails.styles';
import { AuthorImage } from './AuthorImage';
import { AuthorDetailsData } from './model';

type Props = {
    titleTag?: 'h1' | 'h2';
    content: AuthorDetailsData;
    className?: string;
};

export const AuthorDetails: FC<Props> = ({ titleTag = 'h1', content, className }) => {
    const { description, image, name, facebookUrl, linkedinUrl, youtubeUrl, instagramUrl, xUrl } = content;

    const socials = [
        {
            icon: <IconFacebook size="xs" />,
            label: 'Facebook',
            url: facebookUrl,
        },
        {
            icon: <IconLinkedin size="xs" />,
            label: 'LinkedIn',
            url: linkedinUrl,
        },
        {
            icon: <IconYoutube size="xs" />,
            label: 'Youtube',
            url: youtubeUrl,
        },
        {
            icon: <IconInstagram size="xs" />,
            label: 'Instagram',
            url: instagramUrl,
        },
        {
            icon: <IconX size="xs" />,
            label: 'X',
            url: xUrl,
        },
    ].filter((social) => social.url);

    return (
        <StyledAuthorDetails data-section="Author" data-section-name={name} className={className}>
            <AuthorImage image={image?.imageLg?.src} alt={image?.imageLg?.alt} />
            <AuthorDetailsContent>
                <TitleWithImage>
                    <AuthorImage image={image?.imageSm?.src} alt={image?.imageSm?.alt} />
                    <Title tag={titleTag}>{name}</Title>
                </TitleWithImage>
                <Paragraph>{description}</Paragraph>
                <SocialButtons>
                    {socials.map(({ url, icon, label }) => (
                        <CtaLink
                            key={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={label}
                            showIcon={false}
                            iconOnly={true}
                            size="md"
                            variant="secondary"
                            href={url}
                        >
                            {icon}
                        </CtaLink>
                    ))}
                </SocialButtons>
            </AuthorDetailsContent>
        </StyledAuthorDetails>
    );
};
